<template>
  <div class="section-items-panel">
    <!-- Panel Header -->
    <div class="panel-header">
      <div class="row items-center">
        <div class="col">
          <div class="panel-title">
            <q-icon name="tune" color="primary" class="q-mr-sm" />
            {{ displayTitle }}
          </div>
          <div class="panel-subtitle">
            {{ displaySubtitle }}
          </div>
        </div>
        <div v-if="localItems.length > 0" class="col-auto">
          <q-badge color="primary" class="q-pa-sm text-bold">
            {{ localItems.length }} item{{ localItems.length !== 1 ? 's' : '' }}
          </q-badge>
        </div>
      </div>

      <!-- Selection Controls -->
      <div v-if="localItems.length > 0 && !readonly" class="selection-controls q-mt-sm">
        <q-btn
          v-if="selectedItems.length > 0"
          color="negative"
          icon="remove"
          :label="`Remove ${selectedItems.length}`"
          size="sm"
          unelevated
          @click="removeSelectedItems"
        />
        <q-btn
          :color="allSelected ? 'grey' : 'secondary'"
          :icon="allSelected ? 'deselect' : 'select_all'"
          :label="allSelected ? 'Clear' : 'Select All'"
          size="sm"
          flat
          @click="toggleSelectAll"
        />
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="panel-loading text-center">
      <q-spinner color="primary" size="2em" />
      <div class="q-mt-sm text-grey-7">Loading...</div>
    </div>

    <!-- No Section Selected -->
    <div v-else-if="!section" class="panel-empty text-center">
      <q-icon name="tune" size="2em" color="grey-5" />
      <div class="empty-title">No section selected</div>
      <div class="empty-subtitle">
        Select a section to view its items
      </div>
    </div>

    <!-- Empty Section -->
    <div v-else-if="localItems.length === 0" class="panel-empty text-center">
      <q-icon name="tune" size="2em" color="grey-5" />
      <div class="empty-title">No items in section</div>
      <div class="empty-subtitle">
        Add items from the available items panel
      </div>
    </div>

    <!-- Debug Info (if needed) -->
    <div v-else-if="localItems.length && showDebugInfo" class="debug-info q-pa-md">
      <div class="debug-title">Section Items Debug:</div>
      <p>Item count: {{ localItems.length }}</p>
      <pre class="debug-json">{{ JSON.stringify(localItems[0], null, 2) }}</pre>
    </div>

    <!-- Items List with Drag-Drop -->
    <div
      v-else
      ref="itemsListRef"
      class="items-list section-items"
      @mousedown="onMouseDown"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
      @mouseleave="onMouseUp"
    >
      <draggable
        v-model="localItems"
        item-key="id"
        handle=".drag-handle"
        ghost-class="drag-ghost"
        drag-class="dragging"
        :disabled="readonly || !showReorder"
        class="items-grid section-items-grid"
        @end="handleReorder"
      >
        <template #item="{ element, index }">
          <div
            :ref="el => setItemRef(el, index)"
            class="compact-item section-item"
            :class="{
              'item-selected': isSelected(element),
              'item-in-marquee': isInMarquee(index)
            }"
            @click="handleItemClick($event, element)"
          >
            <q-checkbox
              v-if="!readonly"
              :model-value="isSelected(element)"
              size="sm"
              class="item-checkbox"
              @click.stop
              @update:model-value="toggleSelection(element)"
            />
            <q-icon
              v-if="showReorder && !readonly"
              name="drag_indicator"
              class="drag-handle cursor-move"
            />
            <div class="item-info">
              <div class="item-name">{{ getItemName(element) }}</div>
              <div class="item-details-row">
                <span v-if="element.part_number" class="part-number">{{ element.part_number }}</span>
                <span class="item-price">{{ element.quantity }} × ${{ formatPrice(element.unit_price) }}</span>
                <q-badge
                  v-if="getItemType(element)"
                  :color="getTypeColor(element)"
                  size="sm"
                >
                  {{ getItemType(element) }}
                </q-badge>
              </div>
            </div>
            <q-btn
              v-if="!readonly"
              flat
              round
              color="negative"
              icon="remove"
              size="sm"
              class="remove-btn"
              @click.stop="handleRemoveItem(element)"
            >
              <q-tooltip>Remove from section</q-tooltip>
            </q-btn>
          </div>
        </template>
      </draggable>

      <!-- Selection Marquee -->
      <div
        v-if="isDragging"
        class="selection-marquee"
        :style="marqueeStyle"
      ></div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, watch, onBeforeUpdate } from 'vue';
  import draggable from 'vuedraggable';

  const props = defineProps({
    section: {
      type: Object,
      default: null,
    },
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    showReorder: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: '',
    },
    showDebugInfo: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(['remove-item', 'remove-items', 'reorder', 'edit-item', 'update:items']);

  // Local state
  const localItems = ref([]);
  const selectedItems = ref([]);

  // Marquee selection state
  const itemsListRef = ref(null);
  const itemRefs = ref([]);
  const isDragging = ref(false);
  const dragStart = ref({ x: 0, y: 0 });
  const dragCurrent = ref({ x: 0, y: 0 });
  const marqueeIndices = ref(new Set());
  const clickedWithoutDrag = ref(false);

  // Reset item refs before each update
  onBeforeUpdate(() => {
    itemRefs.value = [];
  });

  function setItemRef(el, index) {
    if (el) {
      itemRefs.value[index] = el;
    }
  }

  // Watch items prop
  watch(() => props.items, (newItems) => {
    localItems.value = [...newItems];
    // Remove any selected items that are no longer in the list
    selectedItems.value = selectedItems.value.filter((selected) =>
      newItems.some((item) => item.id === selected.id),
    );
  }, { immediate: true, deep: true });

  // Computed
  const displayTitle = computed(() => {
    if (props.title) {return props.title;}
    if (props.section) {return `Items in "${props.section.section_name || props.section.name || 'Section'}"`;}
    return 'Items in Section';
  });

  const displaySubtitle = computed(() => {
    if (props.readonly) {return 'View only mode';}
    if (!props.showReorder) {return 'Click to select, drag to multi-select';}
    return 'Drag handle to reorder, click to select';
  });

  const allSelected = computed(() => {
    if (localItems.value.length === 0) {return false;}
    return localItems.value.every((item) => isSelected(item));
  });

  // Marquee style computed
  const marqueeStyle = computed(() => {
    if (!isDragging.value) {return {};}

    const left = Math.min(dragStart.value.x, dragCurrent.value.x);
    const top = Math.min(dragStart.value.y, dragCurrent.value.y);
    const width = Math.abs(dragCurrent.value.x - dragStart.value.x);
    const height = Math.abs(dragCurrent.value.y - dragStart.value.y);

    return {
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
    };
  });

  // Methods
  function isSelected(item) {
    return selectedItems.value.some((s) => s.id === item.id);
  }

  function isInMarquee(index) {
    return marqueeIndices.value.has(index);
  }

  function toggleSelection(item) {
    const index = selectedItems.value.findIndex((s) => s.id === item.id);
    if (index >= 0) {
      selectedItems.value.splice(index, 1);
    } else {
      selectedItems.value.push(item);
    }
  }

  function handleItemClick(event, item) {
    // Only toggle if it was a click without drag
    if (clickedWithoutDrag.value) {
      toggleSelection(item);
    }
    clickedWithoutDrag.value = false;
  }

  function toggleSelectAll() {
    if (allSelected.value) {
      selectedItems.value = [];
    } else {
      selectedItems.value = [...localItems.value];
    }
  }

  function removeSelectedItems() {
    if (selectedItems.value.length > 0) {
      selectedItems.value.forEach((item) => {
        emit('remove-item', item);
      });
      selectedItems.value = [];
    }
  }

  function handleRemoveItem(item) {
    emit('remove-item', item);
    // Remove from selection if it was selected
    const index = selectedItems.value.findIndex((s) => s.id === item.id);
    if (index >= 0) {
      selectedItems.value.splice(index, 1);
    }
  }

  function handleReorder() {
    // Update local display_order properties
    localItems.value.forEach((item, index) => {
      item.display_order = index + 1;
    });

    // Emit both reorder event AND update:items for v-model sync
    emit('reorder', localItems.value);
    emit('update:items', [...localItems.value]);
  }

  // Marquee selection handlers
  function onMouseDown(event) {
    // Ignore if clicking on interactive elements
    if (event.target.closest('.q-checkbox, .q-btn, .remove-btn, .drag-handle')) {
      return;
    }

    const container = itemsListRef.value;
    if (!container) {return;}

    const rect = container.getBoundingClientRect();
    const scrollTop = container.scrollTop;

    dragStart.value = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top + scrollTop,
    };
    dragCurrent.value = { ...dragStart.value };
    clickedWithoutDrag.value = true;
  }

  function onMouseMove(event) {
    if (!clickedWithoutDrag.value && !isDragging.value) {return;}

    const container = itemsListRef.value;
    if (!container) {return;}

    const rect = container.getBoundingClientRect();
    const scrollTop = container.scrollTop;

    const currentX = event.clientX - rect.left;
    const currentY = event.clientY - rect.top + scrollTop;

    // Check if we've moved enough to start dragging
    const dx = Math.abs(currentX - dragStart.value.x);
    const dy = Math.abs(currentY - dragStart.value.y);

    if (clickedWithoutDrag.value && (dx > 5 || dy > 5)) {
      // Start dragging
      isDragging.value = true;
      clickedWithoutDrag.value = false;
      marqueeIndices.value = new Set();
    }

    if (isDragging.value) {
      dragCurrent.value = { x: currentX, y: currentY };
      updateMarqueeSelection();
    }
  }

  function onMouseUp() {
    if (isDragging.value) {
      // Apply marquee selection
      marqueeIndices.value.forEach((index) => {
        const item = localItems.value[index];
        if (item && !isSelected(item)) {
          selectedItems.value.push(item);
        }
      });
      marqueeIndices.value = new Set();
    }
    isDragging.value = false;
  }

  function updateMarqueeSelection() {
    if (!itemsListRef.value) {return;}

    const container = itemsListRef.value;
    const containerRect = container.getBoundingClientRect();
    const scrollTop = container.scrollTop;

    // Get marquee bounds
    const marqueeLeft = Math.min(dragStart.value.x, dragCurrent.value.x);
    const marqueeRight = Math.max(dragStart.value.x, dragCurrent.value.x);
    const marqueeTop = Math.min(dragStart.value.y, dragCurrent.value.y);
    const marqueeBottom = Math.max(dragStart.value.y, dragCurrent.value.y);

    const newIndices = new Set();

    itemRefs.value.forEach((el, index) => {
      if (!el) {return;}

      const itemRect = el.getBoundingClientRect();

      // Convert item rect to container-relative coordinates
      const itemLeft = itemRect.left - containerRect.left;
      const itemRight = itemRect.right - containerRect.left;
      const itemTop = itemRect.top - containerRect.top + scrollTop;
      const itemBottom = itemRect.bottom - containerRect.top + scrollTop;

      // Check for intersection
      const intersects =
        itemLeft < marqueeRight &&
        itemRight > marqueeLeft &&
        itemTop < marqueeBottom &&
        itemBottom > marqueeTop;

      if (intersects) {
        newIndices.add(index);
      }
    });

    marqueeIndices.value = newIndices;
  }

  function getItemName(item) {
    return item.description || item.product_name || item.name || item.part_number || 'Unnamed Item';
  }

  function getItemType(item) {
    if (item.is_labor || item.item_type === 'labor' || item.type === 'labor' || item.fromLabor) {
      const laborType = item.labor_type ||
        item.metadata?.original_labor_type ||
        item.metadata?.laborType ||
        'Labor';
      return laborType.replace(/_/g, ' ').split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
    }
    if (item.type && item.type !== 'labor') {
      return item.type.charAt(0).toUpperCase() + item.type.slice(1);
    }
    return null;
  }

  function getTypeColor(item) {
    if (item.is_labor || item.item_type === 'labor' || item.type === 'labor' || item.fromLabor) {
      return 'accent';
    }
    return 'secondary';
  }

  function formatPrice(value) {
    const num = parseFloat(value || 0);
    return num.toFixed(2);
  }
</script>

<style scoped>
.section-items-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.panel-header {
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #f5f5f5;
  flex-shrink: 0;
}

.panel-title {
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-weight: 600;
  color: #1d1d1d;
  margin-bottom: 2px;
}

.panel-subtitle {
  font-size: 0.8rem;
  color: #666;
  margin-left: 32px;
}

.selection-controls {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.items-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  min-height: 0;
  position: relative;
  user-select: none;
}

.panel-loading,
.panel-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
}

.empty-title {
  font-size: 0.9rem;
  font-weight: 500;
  color: #666;
  margin-top: 8px;
}

.empty-subtitle {
  font-size: 0.8rem;
  color: #999;
  margin-top: 4px;
}

.items-grid {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.section-items-grid {
  min-height: 100px;
}

/* Compact Item Row */
.compact-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  gap: 8px;
  transition: all 0.15s ease;
  cursor: pointer;
}

.compact-item:hover {
  background: #f5f5f5;
}

.compact-item.section-item {
  border-left: 3px solid #1976d2;
}

.compact-item.item-selected {
  background: #e3f2fd;
  border-color: #1976d2;
}

.compact-item.item-in-marquee {
  background: #bbdefb;
  border-color: #1976d2;
}

.item-checkbox {
  flex-shrink: 0;
}

.drag-handle {
  cursor: grab;
  color: #9e9e9e;
  flex-shrink: 0;
}

.drag-handle:active {
  cursor: grabbing;
}

.item-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.item-name {
  font-size: 13px;
  font-weight: 500;
  color: #1a1a1a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-details-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
  font-size: 11px;
}

.part-number {
  color: #616161;
  font-family: monospace;
}

.item-price {
  color: #424242;
}

.remove-btn {
  flex-shrink: 0;
  opacity: 0.7;
}

.compact-item:hover .remove-btn {
  opacity: 1;
}

.drag-ghost {
  opacity: 0.4;
  background: #e3f2fd;
}

.dragging {
  opacity: 0.8;
  cursor: move;
}

/* Selection Marquee */
.selection-marquee {
  position: absolute;
  background: rgba(25, 118, 210, 0.15);
  border: 2px dashed #1976d2;
  border-radius: 4px;
  pointer-events: none;
  z-index: 10;
}

.debug-info {
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  margin: 12px;
}

.debug-title {
  font-weight: 600;
  margin-bottom: 6px;
  color: #856404;
}

.debug-json {
  background: #fff;
  padding: 6px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 0.7rem;
  color: #333;
}
</style>
